import { useCallback, useContext, useEffect, useState } from 'react';
import QuizTable from 'src/Components/Admin/QuizTable';
import { downloadScenarioResultsToCSV, getGroup, getSessionScoreCards, getSessionWithSessionId, getUser } from 'src/Services/BorgAPIClient';
import { Group, UserMinimal, Scorecard } from 'src/Constants/Types';
import { AlertColor, Button, Typography } from '@mui/material';
import { removeIllegalChars } from 'src/Util/Util';
import DownloadIcon from '@mui/icons-material/Download';
import { useParams } from 'react-router-dom';
import { getCourseId } from 'src/Constants/ScenarioData';

const QuizPage = ({ onNotification }: {
  onNotification: (message: string, notificationType?: AlertColor) => void,
}) => {
  const { sessionId: sessionIdParam, scenarioId: scenarioIdParam } =
  useParams<{ sessionId: string; scenarioId: string }>();
  
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [quizId, setQuizId] = useState<string | null>(null);

  const [groupData, setGroupData] = useState<Group | null>(null);
  const [quizScoreData, setQuizScoreData] = useState<Scorecard[] | null>(null);
  const [sessionStudents, setSessionStudents] = useState<UserMinimal[] | null>(null);

  const [loading, setLoading] = useState(true);
  const [noData, setNoData] = useState(false);

  useEffect(() => {
    setSessionId(sessionIdParam ?? null);
    setQuizId(scenarioIdParam ?? null);
  }, [sessionIdParam, scenarioIdParam]);

  const createSessionStudentsList = useCallback(async () => {
    if (groupData && groupData.memberIds.length > 0) {
      let studentId;
      let tempSessionStudents: UserMinimal[] = [];

      //Step through members list and get list of students in the group and their names
      for (let i = 0; i < groupData.memberIds.length; i++) {
        studentId = groupData.memberIds[i];

        let response;
        try {
          response = await getUser(studentId.replace(/"|'/g, ''));
        } catch (err) {
          let msg = "Failed to fetch user data";
          console.error(msg, err);
          onNotification(msg, "error");
        }

        if (response?.data) {
          tempSessionStudents[i] = {
            id: groupData.memberIds[i],
            username: response.data.username,
          };
        }
      }

      // Sort the students by their username 
      tempSessionStudents.sort((a, b) => a.username.localeCompare(b.username));

      setSessionStudents(tempSessionStudents);
    }
  }, [groupData]);

  useEffect(() => {
    createSessionStudentsList();
  }, [groupData, createSessionStudentsList]);

  // Obtain Quiz Score Data using the quizId as the scenarioId for getSessionScoreCards api call
  useEffect(() => {
    const fetchQuizScoreData = async () => {

      const groupData = await fetchSessionAndGroup();
      setGroupData(groupData);
      
      if (!groupData || !quizId || !sessionId) return;

      let courseId = "";
      if(groupData && groupData.ownerOrganizationId) {
        courseId = getCourseId(groupData.ownerOrganizationId);
      }
      
      try {
        const quizScoreResponse = await getSessionScoreCards(sessionId, courseId, "", quizId);
        setQuizScoreData(quizScoreResponse.data as Scorecard[]);
      } catch (error) {
        let msg = "Failed to fetch quiz score data";
        console.error(msg, error);
        onNotification(msg, "error");
      }
    };

    fetchQuizScoreData();
  }, [quizId, sessionId]);

  // Set loading to false when quizScoreData is not set
  useEffect(() => {
    if (quizScoreData && quizScoreData?.length > 0) {
      setLoading(false);
    }
  }, [quizScoreData]);

  // Set noData to true if loading takes longer than 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading) {
        setNoData(true);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [loading]);

  const fetchSessionAndGroup = async () => {
    if (!sessionId) return;

    try {
      //Obtain Session Information
      const sessionResponse = await getSessionWithSessionId(sessionId);

      if (sessionResponse.data && sessionResponse.data.groupId) {
        //Obtain Group Information
        const groupResponse = await getGroup(sessionResponse.data.groupId);
        return groupResponse.data;
      }
    } catch (error) {
      let msg = "Failed to fetch course or session data";
      console.error(msg, error);
      onNotification(msg, "error");
    }
  };

  const handleDownloadResultsClick = async (sessionId: string, scenarioId: string, scenarioName: string | undefined) => {
    let filename = scenarioName ? `Quiz//Results - ${scenarioName}.csv` : "Quiz Results.csv";
    filename = removeIllegalChars(filename);
    try {
      await downloadScenarioResultsToCSV(sessionId, scenarioId, filename);
    }
    catch (error) {
      const msg = "Failed to download results";
      console.error(msg, error);
      onNotification(msg, "error");
    }
  }

  const renderQuizTable = (quizScoreData: Scorecard[] | null, sessionStudents: UserMinimal[] | null) => {
    if (sessionStudents && sessionStudents.length > 0) {
      return (<QuizTable quizScoreData={quizScoreData} sessionStudents={sessionStudents} onNotification={onNotification} />)
    }
    return <Typography align='center' variant='h2' sx={{ margin: 10 }}>Loading Students...</Typography>
  }

  // Display loading message if data is not loaded
  if (loading || (!quizScoreData || quizScoreData?.length <= 0)) {
    if (!noData) {
      return <div>Loading...</div>
    } else { // Display message if no data is found after 5 seconds
      return <Typography align='center' variant='h2' sx={{ margin: 10 }}>No quiz data found</Typography>
    }

  } else {  // Display quiz table if data is loaded
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: 600 }}>
          <Button variant="contained" color="primary"
            onClick={() => handleDownloadResultsClick(sessionId!, quizId!, 'Quiz-Results')}
            style={{ display: 'flex', alignItems: 'center' }}
            startIcon={<DownloadIcon />} >
            Download CSV
          </Button>
          <h1>Quiz Responses</h1>
        </div>
        {renderQuizTable(quizScoreData, sessionStudents)}
      </div>
    );
  }
};

export default QuizPage;